import axios from "axios";
import {
  GetOrganizationsDto,
  UpdateOrganizationDto,
  GetOrganizationDto,
  UpdateOrganizationMemberDto,
  InviteCheckStatus,
} from "./organization.types";
import { MemberStatus, PublicUserDto } from "../users/users.types";

const API_URL = `${import.meta.env.VITE_API_BASE_URL}organizations`; // `${import.meta.env.VITE_API_BASE_URL}auth`;

const createOrganization = async (
  organizationName: string,
): Promise<GetOrganizationDto> => {
  const authToken = localStorage.getItem("daex-auth-token");
  const response = await axios.post(
    `${API_URL}`,
    { name: organizationName },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );
  if (response.data) return response.data;
  throw new Error("Fetching failed, no data received.");
};

const checkInvitation = async (
  organizationId: string,
  email: string,
): Promise<InviteCheckStatus> => {
  const authToken = localStorage.getItem("daex-auth-token");
  //@Post(':id/invitations/check/email')

  const response = await axios.post(
    `${API_URL}/${organizationId}/invitations/check/email`,
    { email },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );
  return response.data.status;
};

// Asynchronous function to register a user
const getOrganizations = async (): Promise<GetOrganizationsDto[]> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = axios.get(`${API_URL}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });

  return (await response).data;
};

const getOrganization = async (
  organizationId: string,
): Promise<GetOrganizationDto> => {
  const authToken = localStorage.getItem("daex-auth-token");
  const response = await axios.get(`${API_URL}/${organizationId}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });
  return response.data;
};

export interface respondToInvitationParams {
  organizationId: string;
  status: MemberStatus;
}

const respondToInvitation = async (
  respondToInvitationParams: respondToInvitationParams,
): Promise<MemberStatus> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.put(
    `${API_URL}/${respondToInvitationParams.organizationId}/respondToInvitation`,
    { status: respondToInvitationParams.status },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );

  if (response.data) return response.data;
  throw new Error("Updating failed, no data received.");
};

const updateOrganization = async (
  organizationData: UpdateOrganizationDto,
): Promise<GetOrganizationDto> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.put(
    `${API_URL}/${organizationData.id}`,
    organizationData,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );

  if (response.data) return response.data;
  throw new Error("Updating failed, no data received.");
};

const createOrganizationMember = async (
  email: string,
  organizationId: string,
): Promise<string> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.post(
    `${API_URL}/${organizationId}/inviteUser`,
    { email },
    {
      headers: {
        Authorization: `Bearer ${authToken}`, // Correctly set the Authorization header
      },
    },
  );
  return response.data;
};

const deleteOrganization = async (organizationId: string): Promise<string> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.delete(`${API_URL}/${organizationId}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });
  if (response) return organizationId;
  throw new Error("Updating failed, no data received.");
};

const removeOrganizationMember = async (
  organizationId: string,
  userId: string,
): Promise<string> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.delete(
    `${API_URL}/${organizationId}/removeMember/${userId}`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );
  if (response) return userId;
  throw new Error("Updating failed, no data received.");
};

const leaveOrganization = async (organizationId: string): Promise<string> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.delete(`${API_URL}/${organizationId}/leave`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });
  if (response) return organizationId;
  throw new Error("Updating failed, no data received.");
};

const updateOrganizationMember = async (
  updateData: UpdateOrganizationMemberDto,
): Promise<PublicUserDto> => {
  const authToken = localStorage.getItem("daex-auth-token");
  const response = await axios.put(
    `${API_URL}/${updateData.organizationId}/updateMember`,
    updateData,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );
  if (response) return response.data;
  throw new Error("Updating failed, no data received.");
};

const organizationService = {
  getOrganization,
  getOrganizations,
  createOrganization,
  leaveOrganization,
  updateOrganization,
  deleteOrganization,
  removeOrganizationMember,
  createOrganizationMember,
  updateOrganizationMember,
  respondToInvitation,
  checkInvitation,
};
export default organizationService;
